import { DatePipe } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { DeviceShadowService } from 'src/app/shared/services/device-shadow/device-shadow.service';
import {
  PhotoApiResponse,
  PhotoService,
} from 'src/app/shared/services/photo/photo.service';
import { SsoRedirectService } from 'src/app/shared/services/sso-redirect/sso-redirect.service';
import { VersionsService } from 'src/app/shared/services/versions/versions.service';
import { environment } from '../../../environments/environment';
import { SessionService } from '../../auth/session/session.service';
import { IDeviceMonitoringData } from '../device-monitoring/devicemonitoring.component';
import { ITimelineData } from '../device-timeline/timeline.component';

@Injectable({
  providedIn: 'root',
})
export class MonitoringService {
  url: string = environment.deviceMonitoring;
  motionIntervalUrl: string = environment.motionInterval;
  devicePrivacyUrl: string = environment.devicePrivacy;
  deviceEventUrl: string = environment.deviceEvent;
  deviceEncryptionKeyUrl: string = environment.deviceEncryptionKey;

  PRESENCE_ON_DAYS_IN_MILLISECONDS = 172800000;
  ENCRYPTION_KEY_READER = 'EncryptionKeyReader';

  constructor(
    private httpClient: HttpClient,
    private shadowService: DeviceShadowService,
    private photoService: PhotoService,
    private versionsService: VersionsService,
    private sessionService: SessionService,
    private ssoRedirectService: SsoRedirectService,
    private dateformatter: DatePipe,
  ) {}

  async getMonitoringData(
    serialNumbers: string[],
    startDate: Date,
    endDate: Date,
  ) {
    const metaData = await this.loadDeviceMetadata(serialNumbers);
    // Only fetch battery telemetry when the feature flag is on; each device
    // triggers 7 parallel requests and the battery UI is hidden otherwise.
    const enableBatteryView =
      new URLSearchParams(window.location.search).get('enableBatteryView') === 'true';
    const data = (
      await Promise.all([
        // methods can be added here to load and process additional data types.
        this.loadCheckinData(serialNumbers, startDate, endDate),
        this.loadDevicePhotos(metaData),
        this.loadEcmData(serialNumbers, startDate, endDate),
        this.loadIgnitionData(serialNumbers, startDate, endDate),
        this.loadIgnitionSourcesData(serialNumbers, startDate, endDate),
        this.loadObd2BackoffData(serialNumbers, startDate, endDate),
        this.loadOperationStateData(serialNumbers, startDate, endDate),
        ...(enableBatteryView ? [this.loadBatteryData(serialNumbers, startDate, endDate)] : []),
        this.loadPresenceData(serialNumbers, startDate, endDate),
        this.loadTimelineData(serialNumbers),
        this.loadMotionIntervalData(serialNumbers, startDate, endDate),
        this.loadDevicePrivacyData(serialNumbers, startDate, endDate),
        this.loadDeviceEventData(serialNumbers, startDate, endDate),
        this.loadDeviceVersionData(serialNumbers),
        this.loadDeviceEncryptionKeyData(serialNumbers),
        metaData
      ])
    ).flat();

    return this.groupBySerialNumber(data);
  }

  getTimeline(serialNumber: string): Promise<TimelineResponse> {
    return this.httpClient
      .get<TimelineResponse>(
        `${this.url}/devices/${serialNumber}/timeline/latest`,
      )
      .toPromise();
  }

  getCheckIn(
    serialNumber: string,
    startDate: Date,
    endDate: Date,
  ): Promise<CheckInResponse> {
    return this.httpClient
      .get<CheckInResponse>(
        `${this.url}/devices/${serialNumber}/checkIn?start=${startDate.toISOString()}&end=${endDate.toISOString()}`,
      )
      .toPromise();
  }

  getDevicePrivacyEvents(
    serialNumber: string,
    startDate: Date,
    endDate: Date,
  ): Promise<PrivacyEvent[]> {
    return this.httpClient
      .get<
        PrivacyEvent[]
      >(`${this.devicePrivacyUrl}/devices/${serialNumber}/privacy?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`)
      .toPromise();
  }

  getDeviceEvents(
    serialNumber: string,
    startDate: Date,
    endDate: Date,
  ): Promise<DeviceEvents> {
    return this.httpClient
      .get<DeviceEvents>(
        `${this.deviceEventUrl}/events?serialNumber=${serialNumber}&startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}&size=10000`,
      )
      .toPromise();
  }

  requestEvents(request: CreateEventRequest): Promise<CreateEventResponse> {
    return this.httpClient
      .post<CreateEventResponse>(`${this.deviceEventUrl}/events/vii`, request)
      .toPromise();
  }

  getPresenceDetails(
    serialNumber: string,
    startDate: Date,
    endDate: Date,
  ): Promise<PresenceResponse> {
    return this.httpClient
      .get<PresenceResponse>(
        `${this.url}/devices/${serialNumber}/presence?start=${startDate.toISOString()}&end=${endDate.toISOString()}`,
      )
      .toPromise();
  }

  getOperationalStateDetails(
    serialNumber: string,
    startDate: Date,
    endDate: Date,
  ): Promise<OperationalStateResponse> {
    return this.httpClient
      .get<OperationalStateSegment[]>(
        `${this.url}/devices/${serialNumber}/devicestate?start=${startDate.toISOString()}&end=${endDate.toISOString()}`,
      )
      .toPromise()
      .then((response) => {
        // Wrap the array in the expected interface structure
        return { operationalState: response };
      });
  }

  getIgnitionDetails(
    serialNumber: string,
    startDate: Date,
    endDate: Date,
  ): Promise<IgnitionResponse> {
    return this.httpClient
      .get<IgnitionResponse>(
        `${this.url}/devices/${serialNumber}/ignition?start=${startDate.toISOString()}&end=${endDate.toISOString()}`,
      )
      .toPromise();
  }

  getIgnitionSourcesDetails(
    serialNumber: string,
    startDate: Date,
    endDate: Date,
  ): Promise<IgnitionSourcesResponse> {
    return this.httpClient
      .get<IgnitionSourcesResponse>(
        `${this.url}/devices/${serialNumber}/ignitionSources?start=${startDate.toISOString()}&end=${endDate.toISOString()}`,
      )
      .toPromise();
  }

  getObd2BackoffDetails(
    serialNumber: string,
    startDate: Date,
    endDate: Date,
  ): Promise<Obd2BackoffResponse> {
    return this.httpClient
      .get<Obd2BackoffResponse>(
        `${this.url}/devices/${serialNumber}/obd2Backoff?start=${startDate.toISOString()}&end=${endDate.toISOString()}`,
      )
      .toPromise();
  }

  getEcmDetails(
    serialNumber: string,
    startDate: Date,
    endDate: Date,
  ): Promise<EcmResponse> {
    return this.httpClient
      .get<EcmResponse>(
        `${this.url}/devices/${serialNumber}/ecm?start=${startDate.toISOString()}&end=${endDate.toISOString()}`,
      )
      .toPromise();
  }

  getMotionIntervals(
    serialNumber: string,
    startDate: Date,
    endDate: Date,
  ): Promise<MotionIntervalResponse> {
    return this.httpClient
      .get<MotionIntervalResponse>(
        `${this.motionIntervalUrl}/devices/${serialNumber}/motion/intervals?start=${startDate.toISOString()}&end=${endDate.toISOString()}`,
      )
      .toPromise();
  }

  private getDeviceEncryptionKey(
    serialNumber: string,
  ): Promise<DeviceEncryptionKey> {
    return this.httpClient
      .get<DeviceEncryptionKey>(
        `${this.deviceEncryptionKeyUrl}/encryption-key/${serialNumber}`,
        {
          headers: {
            Authorization: `Bearer ${this.sessionService.getToken()}`,
          },
        },
      )
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 401 && this.sessionService.tokenIsExpired()) {
            this.ssoRedirectService.redirectToSso();
          } else if (error.status === 404) {
            return of({ key: '' });
          }
          return of({ key: undefined });
        }),
      )
      .toPromise();
  }

  private async loadCheckinData(
    serialNumbers: string[],
    startDate: Date,
    endDate: Date,
  ): Promise<IDeviceMonitoringData[]> {
    if (!startDate || !endDate) {
      return [];
    }

    const tasks = serialNumbers.map((s) =>
      this.getCheckIn(s, startDate!, endDate!),
    );

    const result = await Promise.all(tasks);

    const mapToTimelineData = (checkin: CheckInResponse): ITimelineData[] => {
      return checkin.checkin.map((c) => ({
        dataTypes: ['CheckIn'],
        start: new Date(c.checkInDate),
        end: undefined,
        hint: this.dateformatter.transform(c.checkInDate, 'medium'),
      }));
    };

    return result.map((r, i) => ({
      timelineData: mapToTimelineData(r),
      serialNumber: serialNumbers[i],
    }));
  }

  private async loadDeviceEncryptionKeyData(serialNumbers: string[]) {
    const groups = this.sessionService.getCurrentSession().groups;
    const hasRequiredGroup = groups.includes(this.ENCRYPTION_KEY_READER);
    if (hasRequiredGroup) {
      const tasks = serialNumbers.map((s) => this.getDeviceEncryptionKey(s));
      const result = await Promise.all(tasks);
      return result.map((r, i) => ({
        deviceEncryptionKey: r?.key,
        serialNumber: serialNumbers[i],
      }));
    }
    return [];
  }

  private async loadDeviceEventData(
    serialNumbers: string[],
    startDate: Date,
    endDate: Date,
  ) {
    if (!startDate || !endDate) {
      return [];
    }
    const tasks = serialNumbers.map((s) =>
      this.getDeviceEvents(s, startDate!, endDate!),
    );

    const result = await Promise.all(tasks);

    const mapToTimelineData = (deviceEvents: DeviceEvents): ITimelineData[] => {
      return deviceEvents.deviceEvents.map((c) => ({
        dataTypes: ['Events'],
        start: new Date(c.triggerTime),
        end: undefined,
        hint: this.dateformatter.transform(c.triggerTime),
      }));
    };

    return result.map((r, i) => ({
      timelineData: mapToTimelineData(r),
      deviceEvent: r.deviceEvents,
      serialNumber: serialNumbers[i],
    }));
  }

  private async loadDeviceMetadata(
    serialNumbers: string[],
  ): Promise<IDeviceMonitoringData[]> {
    const tasks = serialNumbers.map((s) =>
      this.shadowService.getDeviceShadows({
      serialNumber: s,
      useScroll: false,
      }).then(async (result) => {
      const geotabSerialNumber = result?.results?.[0]?.geotabSerialNumber;
      let geotabMetadata = undefined;

      if(result?.results?.[0]?.deviceType === "GEOTAB")
      {
        geotabMetadata = result?.results?.[0];
        return { metadata: result?.results?.[0],geotabMetadata : result?.results?.[0] };
      }
      
      if (geotabSerialNumber) {
        const geotabResult = await this.shadowService.getDeviceShadows({
        serialNumber: geotabSerialNumber,
        useScroll: false,
        });
        geotabMetadata = geotabResult?.results?.[0];
      }

      const metadata = result?.results?.[0];
      const followerSerial = metadata?.followerSerial;
      if (followerSerial) {
        const hubResult = await this.shadowService.getDeviceShadows({
          serialNumber: followerSerial,
          useScroll: false,
        });
        const hubRecord = hubResult?.results?.[0];
        if (hubRecord && metadata) {
          metadata.hubLastCommunicationDate = hubRecord.lastCommunicationDate;
        }
      }

      return {
        metadata: metadata,
        geotabMetadata: geotabMetadata
      };
      })
    );

    const result = await Promise.all(tasks);

    return result.map((r, i) => ({
      metadata: r.metadata,
      geotabMetadata: r.geotabMetadata,
      serialNumber: serialNumbers[i],
    }));
  }

  private async loadDevicePhotos(
    deviceMetaData: IDeviceMonitoringData[],
  ): Promise<IDeviceMonitoringData[]> {
    const serialNumbers = deviceMetaData.map((d) => {
        return d.metadata?.followerSerial ? [d.serialNumber, d.metadata.followerSerial] : [d.serialNumber]
    });
    const tasks = serialNumbers.map((s) =>
      this.photoService.getPhotoUrls(s).catch(() => [] as PhotoApiResponse[])
    );

    const result = await Promise.all(tasks);

    const mapToPhoto = (photoApiResponse: PhotoApiResponse): IDevicePhoto => ({
      viewName: photoApiResponse.name,
      url: photoApiResponse.signedUrl,
      savedDate: photoApiResponse.photoSavedDate,
    });

    return result.map((r, i) => ({
      photos: r.map(mapToPhoto),
      serialNumber: deviceMetaData[i].serialNumber,
    }));
  }

  private async loadEcmData(
    serialNumbers: string[],
    startDate: Date,
    endDate: Date,
  ): Promise<IDeviceMonitoringData[]> {
    if (!startDate || !endDate) {
      return [];
    }

    const tasks = serialNumbers.map((s) =>
      this.getEcmDetails(s, startDate!, endDate!),
    );

    const result = await Promise.all(tasks);

    const mapToEcmData = (e: EcmRecords): ITimelineData => ({
      dataTypes: e.dataTypes,
      start: e.start,
      end: e.end,
      hint: undefined,
    });

    return result.map((r, i) => ({
      timelineData: r.data.map(mapToEcmData),
      serialNumber: serialNumbers[i],
    }));
  }

  private async loadDevicePrivacyData(
    serialNumbers: string[],
    startDate: Date,
    endDate: Date,
  ): Promise<IDeviceMonitoringData[]> {
    if (!startDate || !endDate) {
      return [];
    }

    const tasks = serialNumbers.map((s) =>
      this.getDevicePrivacyEvents(s, startDate!, endDate!),
    );

    const result = await Promise.all(tasks);

    const mapToTimelineData = (privacy: PrivacyEvent[]): ITimelineData[] => {
      return privacy.map((p) => ({
        dataTypes: ['Privacy - Inside Lens MVAI'],
        start: new Date(p.timestamp),
        end: undefined,
        styles:
          p.privacyModeConfig.inside.mvai === true
            ? 'background-color: green'
            : 'background-color: red',
      }));
    };

    return result.map((r, i) => ({
      timelineData: mapToTimelineData(r),
      serialNumber: serialNumbers[i],
    }));
  }

  private async loadIgnitionData(
    serialNumbers: string[],
    startDate: Date,
    endDate: Date,
  ): Promise<IDeviceMonitoringData[]> {
    if (!startDate || !endDate) {
      return [];
    }

    const tasks = serialNumbers.map((s) =>
      this.getIgnitionDetails(s, startDate!, endDate!),
    );

    const result = await Promise.all(tasks);

    const mapToTimelineData = (ingition: IgnitionResponse): ITimelineData[] => {
      return ingition.ignition.map((i) => ({
        dataTypes: ['Ignition'],
        start: new Date(i.timestamp),
        end: undefined,
        styles:
          i.on === true ? 'background-color: green' : 'background-color: red',
      }));
    };

    return result.map((r, i) => ({
      timelineData: mapToTimelineData(r),
      serialNumber: serialNumbers[i],
    }));
  }

  private async loadIgnitionSourcesData(
    serialNumbers: string[],
    startDate: Date,
    endDate: Date,
  ): Promise<IDeviceMonitoringData[]> {
    if (!startDate || !endDate) {
      return [];
    }

    const tasks = serialNumbers.map((s) =>
      this.getIgnitionSourcesDetails(s, startDate!, endDate!),
    );

    const result = await Promise.all(tasks);

    const mapToTimelineData = (
      response: IgnitionSourcesResponse,
    ): ITimelineData[] => {
      return response.ignitionSources.map((ignitionSourcesEvent) => ({
        dataTypes: ['Ignition Sources'],
        start: new Date(ignitionSourcesEvent.timestamp),
        end: undefined,
        hint: `
          <div>${this.dateformatter.transform(ignitionSourcesEvent.timestamp, 'medium')}</div>
          <div>${ignitionSourcesEvent.sources.join(', ')}</div>
        `,
      }));
    };

    return result.map((r, i) => ({
      timelineData: mapToTimelineData(r),
      serialNumber: serialNumbers[i],
    }));
  }

  private async loadObd2BackoffData(
    serialNumbers: string[],
    startDate: Date,
    endDate: Date,
  ): Promise<IDeviceMonitoringData[]> {
    if (!startDate || !endDate) {
      return [];
    }

    const tasks = serialNumbers.map((s) =>
      this.getObd2BackoffDetails(s, startDate!, endDate!),
    );

    const result = await Promise.all(tasks);

    const mapToTimelineData = (
      response: Obd2BackoffResponse,
    ): ITimelineData[] => {
      return response.obd2Backoff.map((obd2BackoffEvents) => ({
        dataTypes: ['OBD2 Backoff'],
        start: new Date(obd2BackoffEvents.timestamp),
        end: undefined,
        hint: `
          <div>${this.dateformatter.transform(obd2BackoffEvents.timestamp, 'medium')}</div>
          <div>Backoff ${obd2BackoffEvents.backoff ? 'Enabled' : 'Disabled'}</div>
        `,
        styles: obd2BackoffEvents.backoff ? 'background-color: #667892' : null,
      }));
    };

    return result.map((r, i) => ({
      timelineData: mapToTimelineData(r),
      serialNumber: serialNumbers[i],
    }));
  }

  private async loadOperationStateData(
    serialNumbers: string[],
    startDate: Date,
    endDate: Date,
  ): Promise<IDeviceMonitoringData[]> {
    if (!startDate || !endDate) {
      return [];
    }

    startDate = new Date(
      startDate.getTime() - this.PRESENCE_ON_DAYS_IN_MILLISECONDS,
    );
    const tasks = serialNumbers.map((s) =>
      this.getOperationalStateDetails(s, startDate, endDate!),
    );
    const responses = await Promise.all(tasks);
    const data = responses.map((response) => {
      const ops = response.operationalState;
      const stateSegments: ITimelineData[] = [];

      if (ops && ops.length > 0) {
        let segmentStart: Date | undefined = undefined;
        let currentStatus: string | undefined = undefined;

        for (let i = 0; i < ops.length; i++) {
          const event = ops[i];
          if (currentStatus === undefined) {
            currentStatus = event.status;
            segmentStart = new Date(event.timestamp);
          } else if (event.status !== currentStatus) {
            stateSegments.push({
              dataTypes: ['Operating State'],
              start: segmentStart,
              end: new Date(event.timestamp),
              hint: mapToDccState(currentStatus),
              styles: getOperationalStateStyle(currentStatus),
            });
            currentStatus = event.status;
            segmentStart = new Date(event.timestamp);
          }
        }
        if (segmentStart && currentStatus) {
          stateSegments.push({
            dataTypes: ['Operating State'],
            start: segmentStart,
            end: endDate,
            hint: mapToDccState(currentStatus),
            styles: getOperationalStateStyle(currentStatus),
          });
        }
      }
      return stateSegments;
    });
    return data.map((r, i) => ({
      timelineData: r as ITimelineData[],
      serialNumber: serialNumbers[i],
    }));
  }

  private async loadPresenceData(
    serialNumbers: string[],
    startDate: Date,
    endDate: Date,
  ): Promise<IDeviceMonitoringData[]> {
    if (!startDate || !endDate) {
      return [];
    }

    //fetching prior 2 days for the provided date to make sure we get the last presence ON
    startDate = new Date(
      startDate.getTime() - this.PRESENCE_ON_DAYS_IN_MILLISECONDS,
    );
    const tasks = serialNumbers.map((s) =>
      this.getPresenceDetails(s, startDate, endDate!),
    );
    const result = await Promise.all(tasks);
    const data = result.map((p) => {
      let presenceStart: Date | undefined;
      let presenceEnd: Date | undefined;
      const presenceSegments: ITimelineData[] = [];

      p.presence.map((ele, index) => {
        let presenceSegment: ITimelineData | undefined;
        if (ele.online === true && presenceStart === undefined) {
          presenceStart = ele.eventTime;
          if (p.presence[index + 1] === undefined) {
            presenceSegment = {
              dataTypes: ['Connection'],
              start: presenceStart,
              end: endDate,
              styles: `background-color: orange`,
            };
          }
        }
        if (ele.online === false && presenceStart !== undefined) {
          presenceEnd = ele.eventTime;
          presenceSegment = {
            dataTypes: ['Connection'],
            start: presenceStart,
            end: presenceEnd,
            styles: `background-color: orange`,
          };
        }
        if (presenceSegment !== undefined) {
          presenceSegments.push(presenceSegment);
          presenceStart = undefined;
          presenceEnd = undefined;
        }
      });

      return presenceSegments;
    });
    return data.map((r, i) => ({
      timelineData: r as ITimelineData[],
      serialNumber: serialNumbers[i],
    }));
  }

  private async loadTimelineData(
    serialNumbers: string[],
  ): Promise<IDeviceMonitoringData[]> {
    const tasks = serialNumbers.map((s) => this.getTimeline(s));

    const result = await Promise.all(tasks);

    const decorateTimelines = (t: TimelineSegment): ITimelineData => ({
      dataTypes: t.dataTypes,
      end: t.end,
      start: t.start,
      hint: undefined,
    });

    return result.map((r, i) => ({
      timelineData: r.timeline.map(decorateTimelines),
      serialNumber: serialNumbers[i],
    }));
  }

  private async loadMotionIntervalData(
    serialNumbers: string[],
    startDate: Date,
    endDate: Date,
  ): Promise<IDeviceMonitoringData[]> {
    if (!startDate || !endDate) {
      return [];
    }

    const tasks = serialNumbers.map((s) =>
      this.getMotionIntervals(s, startDate, endDate),
    );

    const result = await Promise.all(tasks);

    const mapToTimelineData = (e: MotionInterval): ITimelineData => ({
      dataTypes: ['Motion_Interval'],
      start: e.start,
      end: e.end,
      styles: `background-color: purple`,
    });

    return result.map((r, i) => ({
      timelineData: r.motionIntervals.map(mapToTimelineData),
      serialNumber: serialNumbers[i],
    }));
  }

  private async loadDeviceVersionData(
    serialNumbers: string[],
  ): Promise<IDeviceMonitoringData[]> {
    const versionsPromises = serialNumbers.map(async (serialNumber) => {
      try {
        return await this.versionsService.getDeviceVersion(serialNumber);
      } catch (error) {
        console.error(error);
        return undefined;
      }
    });
    const versions = await Promise.all(versionsPromises);

    return versions.map((version, index) => ({
      serialNumber: serialNumbers[index],
      deviceVersion: version,
    }));
  }

  getBatteryTimeline<T>(
    serialNumber: string,
    parameter: string,
    startDate: Date,
    endDate: Date,
  ): Promise<T[]> {
    return this.httpClient
      .get<T[] | null | undefined>(
        `${this.url}/devices/${serialNumber}/battery?parameter=${encodeURIComponent(parameter)}&start=${startDate.toISOString()}&end=${endDate.toISOString()}`,
      )
      .toPromise()
      .then((response) => (Array.isArray(response) ? response : []));
  }

  private async loadBatteryData(
    serialNumbers: string[],
    startDate: Date,
    endDate: Date,
  ): Promise<IDeviceMonitoringData[]> {
    if (!startDate || !endDate) {
      return [];
    }

    const tasks = serialNumbers.map(async (s) => {
      const [
        voltage,
        charger,
        warnings,
        internalResistance,
        recharge,
        rtcLoss,
        health,
      ] = await Promise.all([
        this.getBatteryTimeline<VoltageTimelineItem>(s, 'voltage', startDate, endDate),
        this.getBatteryTimeline<ChargerTimelineItem>(s, 'charger', startDate, endDate),
        this.getBatteryTimeline<BatteryWarningTimelineItem>(s, 'warnings', startDate, endDate),
        this.getBatteryTimeline<InternalResistanceTimelineItem>(s, 'internalResistance', startDate, endDate),
        this.getBatteryTimeline<RechargeTimelineItem>(s, 'recharge', startDate, endDate),
        this.getBatteryTimeline<RtcLossTimelineItem>(s, 'rtcLoss', startDate, endDate),
        this.getBatteryTimeline<BatteryHealthTimelineItem>(s, 'health', startDate, endDate),
      ]);

      const timelineData: ITimelineData[] = [];

      const pushPoints = (
        items: { timestamp: string }[] | undefined,
        label: string,
      ) => {
        (items ?? []).forEach((item) =>
          timelineData.push({
            dataTypes: [label],
            start: new Date(item.timestamp),
            end: undefined,
          }),
        );
      };

      pushPoints(voltage, 'Voltage');
      pushPoints(charger, 'Charger On/Off');
      pushPoints(warnings, 'Battery Warnings');
      pushPoints(internalResistance, 'Internal Resistance');
      pushPoints(recharge, 'Recharge Events');
      pushPoints(rtcLoss, 'RTC-loss');

      // Battery Health renders as colored segments with a tooltip, mirroring the
      // Operating State row and reusing the Insights battery-health colors/labels.
      timelineData.push(...buildBatteryHealthSegments(health ?? [], endDate));

      return timelineData;
    });

    const results = await Promise.all(tasks);

    return results.map((timelineData, i) => ({
      timelineData,
      serialNumber: serialNumbers[i],
    }));
  }

  private groupBySerialNumber(
    data: IDeviceMonitoringData[],
  ): Map<string, IDeviceMonitoringData> {
    return data.reduce(
      (
        dataBySerial: Map<string, IDeviceMonitoringData>,
        d: IDeviceMonitoringData,
      ) => {
        if (!dataBySerial.get(d.serialNumber)) {
          dataBySerial.set(d.serialNumber, {
            serialNumber: d.serialNumber,
            timelineData: [],
          });
        }

        const monitoringData = dataBySerial.get(d.serialNumber)!;

        if (d.metadata !== undefined) {
          monitoringData.metadata = d.metadata;
        }

        if (d.geotabMetadata !== undefined) {
          monitoringData.geotabMetadata = d.geotabMetadata;
        }

        if (d.photos !== undefined) {
          monitoringData.photos = d.photos;
        }
        if (d.deviceVersion !== undefined) {
          monitoringData.deviceVersion = d.deviceVersion;
        }
        if (d.timelineData?.length > 0) {
          monitoringData.timelineData = monitoringData.timelineData.concat(
            d.timelineData,
          );
        }
        if (d.deviceEvent) {
          monitoringData.deviceEvent = d.deviceEvent;
        }
        if (d.deviceEncryptionKey !== undefined) {
          monitoringData.deviceEncryptionKey = d.deviceEncryptionKey;
        }

        dataBySerial.set(d.serialNumber, monitoringData);

        return dataBySerial;
      },
      new Map<string, IDeviceMonitoringData>(),
    );
  }
}

function getOperationalStateStyle(status: string): string {
  switch (status) {
    case 'ActiveIgnitionOn':
      return 'background-color: #1B8E6D';
    case 'ActiveIgnitionUnknown':
      return 'background-color: #00C6D2';
    case 'ActiveIgnitionOff':
      return 'background-color: #71B3B4';
    case 'HibernationWake':
      return 'background-color: #4C84FF';
    case 'Hibernation':
      return 'background-color: #7D7FFF';
    case 'DeepSleep':
      return 'background-color: #4A2DA6';
    case 'CellularDisconnect':
      return 'background-color: #F94E4E';
    default:
      return 'background-color: black';
  }
}

function mapToDccState(backendState: string): string {
  switch (backendState) {
    case 'ActiveIgnitionOn':
      return '<span>Active<br>Fully engaged; driving state.</span>';
    case 'ActiveIgnitionUnknown':
      return '<span>Awake<br>Transmitting data and recording.</span>';
    case 'ActiveIgnitionOff':
      return '<span>Awake (Countdown)<br>Hibernation countdown in progress.</span>';
    case 'HibernationWake':
      return '<span>Communicating<br>Data-only transmission; no video recording.</span>';
    case 'Hibernation':
      return '<span>Hibernating<br>Low power, periodic auto-Communicating state.</span>';
    case 'DeepSleep':
      return '<span>Deep Sleep<br>Very Low power, periodic auto-Communicating state.</span>';
    case 'CellularDisconnect':
      return '<span>Offline<br>No cellular connection.</span>';
    default:
      return backendState;
  }
}

// Battery Health segment colors mirror BATTERY_HEALTH_DOT_COLORS in the Insights
// Device Battery table (insights/pages/shadow-battery/device-battery-insights.component.ts).
function getBatteryHealthStyle(batteryHealth: number): string {
  switch (batteryHealth) {
    case 0: // Good
      return 'background-color: #00B884';
    case 1: // Weak
      return 'background-color: #FFD600';
    case 2: // Critical
      return 'background-color: #F94E4E';
    case 3: // Not Charging
      return 'background-color: #F94E4E';
    default:
      return 'background-color: lightslategray';
  }
}

// Tooltip copy mirrors the battery-health column (BATTERY_HEALTH_DISPLAY) in the
// Insights Device Battery table so both surfaces read the same mapped values.
function mapToBatteryHealthDisplay(batteryHealth: number): string {
  switch (batteryHealth) {
    case 0:
      return 'Normal Battery';
    case 1:
      return 'Weak Battery';
    case 2:
      return 'Faulty Battery';
    case 3:
      return 'Not Charging';
    default:
      return 'Unknown';
  }
}

// Collapse consecutive same-health records into colored segments, exactly like
// loadOperationStateData builds Operating State segments.
function buildBatteryHealthSegments(
  items: BatteryHealthTimelineItem[],
  endDate: Date,
): ITimelineData[] {
  const segments: ITimelineData[] = [];
  if (!items || items.length === 0) {
    return segments;
  }

  let segmentStart: Date | undefined;
  let currentHealth: number | undefined;

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (currentHealth === undefined) {
      currentHealth = item.batteryHealth;
      segmentStart = new Date(item.issueTimestamp);
    } else if (item.batteryHealth !== currentHealth) {
      segments.push({
        dataTypes: ['Battery Health'],
        start: segmentStart!,
        end: new Date(item.issueTimestamp),
        hint: `<span>${mapToBatteryHealthDisplay(currentHealth)}</span>`,
        styles: getBatteryHealthStyle(currentHealth),
      });
      currentHealth = item.batteryHealth;
      segmentStart = new Date(item.issueTimestamp);
    }
  }

  if (segmentStart && currentHealth !== undefined) {
    segments.push({
      dataTypes: ['Battery Health'],
      start: segmentStart,
      end: endDate,
      hint: `<span>${mapToBatteryHealthDisplay(currentHealth)}</span>`,
      styles: getBatteryHealthStyle(currentHealth),
    });
  }

  return segments;
}

export interface OperationalStateSegment {
  serialNumber: string;
  status: string;
  timestamp: Date;
}

export interface OperationalStateResponse {
  operationalState: OperationalStateSegment[];
}

// Battery timeline item shapes — one per `parameter` returned by
// GET /devices/{serial}/battery (see devicemonitoring-api BatteryParameter).
export interface VoltageTimelineItem {
  timestamp: string;
  cvBeforeChargeStart: number;
  cvAfterChargeEnd: number;
  cvLastChargeOff: number;
}

export interface ChargerTimelineItem {
  timestamp: string;
  totalChargeEnabledSeconds: number;
  measureDuration: number;
  chargerOn: boolean;
}

export interface BatteryWarningTimelineItem {
  timestamp: string;
  totalLowVoltageFaultDurationSeconds: number;
  totalChargeFaultWhileChargingSeconds: number;
  totalChargeFaultWhileNotChargingSeconds: number;
  totalChargeConditionsNotMetSeconds: number;
  hasWarning: boolean;
}

export interface InternalResistanceTimelineItem {
  timestamp: string;
  averageInternalResistance: number;
}

export interface RechargeTimelineItem {
  timestamp: string;
  lowVoltageRechargeAttempts: number;
}

export interface RtcLossTimelineItem {
  timestamp: string;
  rtcLoss: boolean;
}

export interface BatteryHealthTimelineItem {
  serialNumber: string;
  issueTimestamp: string;
  reason: string;
  batteryHealth: number;
}
export interface PresenceSegment {
  online: boolean;
  eventTime: Date;
  updatedDate: Date;
  status: string;
}

export interface PresenceResponse {
  presence: PresenceSegment[];
}

export interface CheckInResponse {
  checkin: CheckInDate[];
}

export interface CheckInDate {
  checkInDate: string;
}

export interface PrivacyEvent {
  privacyModeConfig: {
    inside: {
      mvai: boolean;
    };
  };
  timestamp: Date;
}

export interface DeviceEncryptionKey {
  key: string;
}

export interface DeviceEvents {
  deviceEvents: DeviceEvent[];
  totalCount: number;
}

export interface DeviceEvent {
  id: string;
  serialNumber: string;
  safetyCustomerEventId: string;
  triggerTime: string;
  triggerType: number;
  dceRequestStatus: string;
  viiRequestId: string;
  viiActor: string;
  viiRequestDate: string;
  shouldIngestEvent?: boolean;
}

export interface TimelineResponse {
  serialNumber?: string;
  timeline: TimelineSegment[];
}

export interface TimelineSegment {
  start: Date;
  end: Date;
  dataTypes: string[];
}

export interface IgnitionResponse {
  ignition: IgnitionSegments[];
}

export interface IgnitionSourcesResponse {
  ignitionSources: IgnitionSourcesRecord[];
}

export interface IgnitionSourcesRecord {
  sources: string[];
  serialNumber: string;
  companyId: number;
  state: boolean;
  timestamp: string;
}

export interface Obd2BackoffResponse {
  obd2Backoff: Obd2BackoffRecord[];
}

export interface Obd2BackoffRecord {
  backoff: boolean;
  serialNumber: string;
  companyId: number;
  timestamp: string;
}

export interface IgnitionSegments {
  on: boolean;
  inferred: boolean;
  latitude: number;
  longitude: number;
  speed: number;
  heading: number;
  timestamp: string;
}

export interface EcmResponse {
  data: EcmRecords[];
}

export interface EcmRecords {
  start: Date;
  end: Date;
  dataTypes: string[];
}

export interface MotionInterval {
  start: Date;
  end: Date;
}

export interface MotionIntervalResponse {
  motionIntervals: MotionInterval[];
}

export interface IDevicePhoto {
  viewName: string;
  url: string;
  savedDate: Date;
}

export interface CreateEventResponse {
  viiRequestId: string;
}

export interface CreateEventRequest {
  docIds: string[];
  actor: string;
}
